/* eslint-disable no-console */
import React from 'react';
import { Flex, Text } from '@chakra-ui/react';
import { Modal, Button, Checkbox, FormFeedback } from '@morressier/alembic';
import { useForm } from 'react-hook-form';
import _ from 'lodash';
import {
  FormFieldDefinition,
  FormFieldCheckboxArray
} from '@publishing-engine/domain/types/article';
import { ajvResolver } from '@hookform/resolvers/ajv';
import { generateValidationSchema } from '../../../../utils/validation';

interface Props {
  isOpen: boolean;
  onSubmit: (value: string[]) => void;
  onCancel: () => void;
  formField: FormFieldCheckboxArray;
  value: string[];
}

type ICheckboxFormState = {
  [key: string]: string[];
};

const CheckboxUpdateModal: React.FC<Props> = props => {
  const { isOpen, onSubmit, onCancel, formField } = props;

  // Default form values
  const defaultValues = _.set(
    {} as ICheckboxFormState,
    formField.caseDataFields.main,
    props.value
  );

  // Schema validation
  const schema = generateValidationSchema({
    [formField.name]: formField
  } as Record<string, FormFieldDefinition>);

  const methods = useForm({
    defaultValues,
    resolver: ajvResolver(schema)
  });

  const {
    handleSubmit,
    watch,
    setValue,
    clearErrors,
    formState: { errors }
  } = methods;

  console.log('CheckboxUpdateModal errors:', errors);

  const fieldName = formField.caseDataFields.main;
  const mainField = `${fieldName}` as const;
  const fieldValue: string[] = watch(mainField) || [];

  const handleCheckboxChange = (checked: boolean, itemValue: string) => {
    clearErrors(mainField);

    let newValue: string[] = [];

    if (checked) {
      if (itemValue === 'not_applicable') {
        // Exclusive option
        newValue = ['not_applicable'];
      } else {
        newValue = [
          ...fieldValue.filter(v => v !== 'not_applicable'),
          itemValue
        ];
      }
    } else {
      newValue = fieldValue.filter(v => v !== itemValue);
    }

    setValue(mainField, newValue, {
      shouldValidate: true,
      shouldDirty: true,
      shouldTouch: true
    });
  };

  const options = Object.keys(formField.params.labels.options).map(
    optionKey => ({
      value: optionKey,
      children: (
        <Flex flexDirection="column">
          <Text textStyle="textBody">
            {formField.params.labels.options[optionKey]?.main}
          </Text>
          {formField.params.labels.options[optionKey]?.secondary && (
            <Text textStyle="textBody">
              {formField.params.labels.options[optionKey]?.secondary}
            </Text>
          )}
        </Flex>
      )
    })
  );

  return (
    <Modal
      isOpen={isOpen}
      onClose={onCancel}
      width="755px"
      closeButtonProps={{
        hidden: true
      }}
    >
      <Flex
        padding={8}
        gap={4}
        bgColor="surfacePrimaryFill"
        flexDirection="column"
        borderRadius="base"
      >
        <form
          onSubmit={handleSubmit(() => {
            onSubmit(fieldValue);
          })}
        >
          <Flex marginBottom={4}>
            <Text textStyle="headingSection">{`Update ${formField.params.labels.main}`}</Text>
          </Flex>

          <Flex
            padding={8}
            gap={6}
            bgColor="surfacePrimaryFill"
            flexDirection="column"
            borderRadius="base"
            flex={1}
          >
            <Flex gap={4} flexDirection="column" marginBottom={4}>
              {options.map(opt => (
                <Checkbox
                  key={opt.value}
                  id={opt.value}
                  name={mainField}
                  ariaLabel={mainField}
                  isChecked={fieldValue.includes(opt.value)}
                  onChange={({ target: { checked } }) =>
                    handleCheckboxChange(checked, opt.value)
                  }
                >
                  {opt.children}
                </Checkbox>
              ))}
            </Flex>
          </Flex>
          {_.get(errors, mainField) && (
            <FormFeedback variant="error">
              {_.get(errors, mainField)?.message as string}
            </FormFeedback>
          )}
          <Flex
            gap={2}
            justifyContent="flex-end"
            alignItems="center"
            alignSelf="stretch"
          >
            <Button variant="secondary" onClick={onCancel} borderRadius="small">
              Cancel
            </Button>
            <Button variant="primary" type="submit" borderRadius="small">
              Save Changes
            </Button>
          </Flex>
        </form>
      </Flex>
    </Modal>
  );
};

export default CheckboxUpdateModal;
